/**
 * BuiltinAliasFactory implementations for preference-driven alias
 * templates.
 *
 * Each factory closes over a per-user preferences `LiveData` and
 * ships a `BuiltinAliasFactory` to the rows-side `SheetModelCache`.
 * The preference dependency is declared on the template
 * (`reexpandOn` — an invalidation path pattern); the accessor's
 * generic `wireDeclaredReexpand` turns the declaration into the live
 * sink subscription, so there is no hand-written listener to drift
 * from. The rows-side stays preferences-agnostic — it just hosts
 * whatever templates the layer above gives it (R6.2).
 */

import { Rational } from "@tento-core/rational";
import {
  type AliasProps,
  type BuiltinAliasFactory,
  type ChildSpec,
  CalendarPreferencesChanged,
  type Option,
  RowNetworkKey,
} from "@tento-lona/sheets";
import type { CalendarPreferences } from "./calendar-preferences.ts";
import { WeatherLocations } from "../weather-locations.ts";

const DEFAULT_WEATHER_ALIAS_TEMPLATE = "alias.default-weather";
/** `TimezoneChanged`'s path: the viewer's zone, which decides their place. */
const TIMEZONE_PATH = "user.preferences.timezone";

/**
 * `alias.secondary-timezones` — one `timezone` alias child per
 * entry in the user's `secondaryTimezones` calendar preference. Used
 * by the calendar legend walker to render secondary tz badges.
 *
 * Re-expansion fires whenever the preference's invalidation path
 * fires (declared via `reexpandOn`), so adding/removing a tz in
 * settings propagates without a page reload.
 */
export function secondaryTimezonesAliasFactory(
  prefs: CalendarPreferences,
): BuiltinAliasFactory {
  const templateName = "alias.secondary-timezones";
  return {
    templateName,
    template: {
      displayName: "Secondary timezones",
      // Row-node half: a tz-list change re-expands this template's
      // instances. The app ALSO registers this preference as
      // render-geometry (tz rows change label/column geometry), so both
      // routes fire — declared consumption may overlap.
      reexpandOn: [
        CalendarPreferencesChanged.pathFor("secondaryTimezones"),
      ],
      rule: { kind: "static", children: [] },
      dynamicTail: {
        // Omitted `parentPreset` → tz aliases attach directly under
        // the alias root (R6.2).
        items: () =>
          prefs.peek().secondaryTimezones.map((tz) => ({ tz })),
        as: (item, _props, index): ChildSpec => {
          const tz = typeof (item as { tz?: unknown }).tz === "string"
            ? (item as { tz: string }).tz
            : "";
          return {
            preset: `tz-${tz.replace(/\//g, "-")}`,
            // Instantiate the `alias.timezone` template (canonical alias-child
            // shape: `type: "alias"` + `attributes.template` + `props`). The
            // bare `type: "timezone"` is a pre-`alias.*`-rename name that no
            // longer dispatches (DESCRIPTOR_BUILDERS keys on Alias.TIMEZONE =
            // "alias.timezone"), so it fell back to an unknown text source —
            // not an alias, so the calendar legend walker skipped it (no tz
            // badge) and it surfaced the "?" unknown-row icon.
            type: "alias",
            attributes: { template: "alias.timezone", props: { tz } },
            position: Rational.nth(index),
            storage: "own",
            placement: "child",
          };
        },
      },
    },
  };
}

/**
 * `alias.default-weather` — the user's default weather row.
 *
 * Every alias instance using this template tracks
 * `prefs.live.defaultWeatherLocation`; updating the preference fans out
 * to every sheet via the declared `reexpandOn` path. Instances that
 * carry an explicit `props.locationId` (e.g. legacy persisted rows
 * migrated by
 * `2026-04-26-000000_backfill_weather_alias_template`) override the
 * preference until the prop is cleared — so the migration to this
 * template doesn't reset users who had a pinned location. With neither
 * (the preference unset) the location is the viewer's, determined at
 * expansion time exactly as a new weather row's is: `viewerLocation`
 * (see `viewerWeatherLocationOf`). When that is `null` too the alias names
 * no location: no data address, no children — a labelled row of empty
 * cells, nothing read. Nothing is written back to the preference.
 */
export function defaultWeatherAliasFactory(
  prefs: CalendarPreferences,
  viewerLocation: () => Option<string> = () => null,
): BuiltinAliasFactory {
  const templateName = DEFAULT_WEATHER_ALIAS_TEMPLATE;
  // Location resolution: an explicit `locationId` prop (legacy pinned rows)
  // wins; then the live preference; then the viewer's place.
  const resolveLid = (props: AliasProps): Option<string> => {
    const propLid = typeof props.locationId === "string" &&
        props.locationId.length > 0
      ? props.locationId
      : null;
    return propLid ?? prefs.peek().defaultWeatherLocation ?? viewerLocation();
  };
  return {
    templateName,
    template: {
      displayName: "Weather",
      // Data address (data-address migration Phase A): the preference
      // resolves to a concrete location, emitted as the EXPLICIT
      // owner-scoped `[weather@lona.so]:~weather:{lid}` — the same
      // single-producer form as `alias.weather`'s `dataRef`, so both
      // templates share one `canonicalCells` entry per location.
      dataRef: (props) => ({
        lookupKey: weatherKeyOf(resolveLid(props)),
        localPath: null,
      }),
      // Row-node half of preference routing (see AliasTemplate.
      // reexpandOn): the location change re-expands THIS template's
      // instances — row-scoped, no whole-sheet clone.
      reexpandOn: [
        CalendarPreferencesChanged.pathFor("defaultWeatherLocation"),
        // The viewer's place follows their timezone.
        TIMEZONE_PATH,
      ],
      rule: {
        kind: "static",
        children: (props, _ctx): readonly ChildSpec[] => {
          const lid = resolveLid(props);
          // No location chosen: no source, so no weather is read for a place nobody named.
          if (lid == null) return [];
          // Weather is location-only: a single self-painting `data-source`
          // (explicit `[weather@lona.so]:~weather:{lid}` + `column.weather`
          // renderer), re-expanded on `defaultWeatherLocation` change (see
          // `reexpandOn` above). This alias inlines in flatten
          // (`isTransparentDataAlias` — its one child is a source), so the
          // concrete weather source row renders directly. Sync logs are
          // NOT authored here: any source row (the weather source included)
          // grows a nested `logs` child on demand via the generic
          // `Sheet.reconcileSyncLogsChild` mechanism, toggled by the "Show Sync
          // Logs" row option. Placement (body vs header) is the catalog's job —
          // the `:calendar` sheet authors this alias under its header `surface`.
          const weatherSource: ChildSpec = {
            preset: "weather",
            type: "data-source",
            label: "Weather",
            lookupKey: RowNetworkKey.weatherDataKey(lid),
            position: Rational.nth(0),
            storage: "own",
            placement: "child",
            attributes: weatherSourceAttributes(lid),
            children: [
              {
                preset: "renderer-column",
                type: "column.weather",
                position: Rational.nth(0),
                storage: "parent",
                placement: "child",
              },
            ],
          };
          return [weatherSource];
        },
      },
    },
  };
}

/** `lid`'s weather data address; `""` (`AliasTemplate.dataRef`'s "none") for no location. */
function weatherKeyOf(lid: Option<string>): string {
  return lid == null ? "" : RowNetworkKey.weatherDataKey(lid);
}

function weatherSourceAttributes(
  lid: string,
): NonNullable<ChildSpec["attributes"]> {
  const numeric = Number(lid);
  return Number.isFinite(numeric) && String(numeric) === lid
    ? { dtype: "obj", lid: numeric }
    : { dtype: "obj" };
}

/**
 * The viewer's weather location id for `alias.default-weather` with no
 * preference set: `WeatherLocations.forViewer(table, tzid)` — the same rule a
 * new weather row's location follows — read at call time, or `null` while
 * the table has not loaded, for a zone that names no city, or a `null` zone.
 */
export function viewerWeatherLocationOf(
  table: () => Option<WeatherLocations>,
  tzid: () => Option<string>,
): () => Option<string> {
  return () => {
    const loaded = table();
    if (loaded == null) return null;
    const location = WeatherLocations.forViewer(loaded, tzid());
    return location == null ? null : String(location.id);
  };
}
